import api from '@/lib/api';

/**
 * Upload a browser blob: preview URL as a persisted scene image (S3).
 * @param {string} blobUrl
 * @returns {Promise<string>} HTTPS image URL
 */
/**
 * Upload a local image file as a persisted scene image (S3).
 * @param {File} file
 * @returns {Promise<string>} HTTPS image URL
 */
export async function uploadSceneImageFromFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post('/narration/image-story/upload-scene-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const url = data?.image_url || data?.source_image_url;
  if (!url) {
    throw new Error('Upload did not return an image URL');
  }
  return url;
}

/** Upload a Blob/File directly (avoids fetch(blob:) which CSP blocks in Electron file:// apps). */
export async function uploadSceneImageFromBlob(blob, filename = 'scene-crop.png') {
  if (!(blob instanceof Blob)) {
    throw new Error('uploadSceneImageFromBlob expects a Blob');
  }
  const formData = new FormData();
  formData.append('file', blob, filename);
  const { data } = await api.post('/narration/image-story/upload-scene-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const url = data?.image_url || data?.source_image_url;
  if (!url) {
    throw new Error('Upload did not return an image URL');
  }
  return url;
}

export async function uploadSceneImageFromBlobUrl(blobUrl) {
  if (!blobUrl?.startsWith?.('blob:')) {
    return blobUrl;
  }
  let blob;
  try {
    const response = await fetch(blobUrl);
    blob = await response.blob();
  } catch {
    blob = await readBlobUrlViaXhr(blobUrl);
  }
  return uploadSceneImageFromBlob(blob);
}

/**
 * Upload a local audio file as persisted scene narration (S3).
 * @param {File} file
 */
export async function uploadSceneAudioFromFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post('/narration/image-story/upload-scene-audio', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const url = data?.audio_url || data?.url;
  if (!url) {
    throw new Error('Upload did not return an audio URL');
  }
  return {
    ...data,
    audio_url: url,
    audio_duration: Number(data?.audio_duration || data?.duration) || 0,
    duration: Number(data?.audio_duration || data?.duration) || 0,
  };
}

function readBlobUrlViaXhr(blobUrl) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', blobUrl);
    xhr.responseType = 'blob';
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300 && xhr.response) {
        resolve(xhr.response);
        return;
      }
      reject(new Error('Could not read blob URL for upload'));
    };
    xhr.onerror = () => reject(new Error('Could not read blob URL for upload'));
    xhr.send();
  });
}

function revokeBlobIfNeeded(url) {
  if (url?.startsWith?.('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Replace blob: image URLs in narration source segments with server URLs.
 * @param {Array<Record<string, unknown>>} segments
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function ensureSegmentImageUrlsAreRemote(segments) {
  if (!Array.isArray(segments) || !segments.length) return segments;

  const out = [];
  for (const seg of segments) {
    const prevImage = seg.image_url;
    const prevSource = seg.source_image_url;
    let imageUrl = prevImage;
    let sourceUrl = prevSource;

    if (typeof imageUrl === 'string' && imageUrl.startsWith('blob:')) {
      imageUrl = await uploadSceneImageFromBlobUrl(imageUrl);
      revokeBlobIfNeeded(prevImage);
    }
    if (typeof sourceUrl === 'string' && sourceUrl.startsWith('blob:')) {
      if (sourceUrl === prevImage && imageUrl && !imageUrl.startsWith('blob:')) {
        sourceUrl = imageUrl;
      } else {
        sourceUrl = await uploadSceneImageFromBlobUrl(sourceUrl);
      }
      revokeBlobIfNeeded(prevSource);
    }

    out.push({
      ...seg,
      image_url: imageUrl || null,
      source_image_url: sourceUrl || imageUrl || null,
    });
  }
  return out;
}
